import { useEffect, useRef, useState } from 'react'
import { api } from '@/api/client'
import { Icon } from '@/components/Icon'
import { transcribeAudio } from '@/lib/intakeAudio'

const MAX_AUDIO_BYTES = 100 * 1024 * 1024
const AUDIO_EXTENSIONS = /\.(mp3|wav|m4a|aac|ogg|opus|flac|webm|aiff?|wma)$/i

export function IntakeAudioUpload({ base, disabled, onActive, onTranscript }: {
  base: string; disabled: boolean; onActive: (active: boolean) => void; onTranscript: (text: string) => void
}) {
  const input = useRef<HTMLInputElement>(null)
  const controller = useRef<AbortController | null>(null)
  const callbacks = useRef({ onActive, onTranscript })
  callbacks.current = { onActive, onTranscript }
  const [recording, setRecording] = useState<File | null>(null)
  const [active, setActive] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => () => { controller.current?.abort() }, [])

  async function upload(file: File) {
    if (disabled || controller.current) return
    setError(''); setRecording(file)
    if (!file.size || file.size > MAX_AUDIO_BYTES) { setError('Choose a nonempty recording up to 100 MiB.'); return }
    if (!file.type.startsWith('audio/') && !AUDIO_EXTENSIONS.test(file.name)) { setError('Choose an audio file such as MP3, WAV or M4A.'); return }
    const request = new AbortController()
    controller.current = request; setActive(true); callbacks.current.onActive(true)
    try {
      const { token } = await api.post<{ token: string }>(`${base}/audio-token`, undefined, { signal: request.signal })
      const text = await transcribeAudio(file, token, request.signal)
      if (!request.signal.aborted) { callbacks.current.onTranscript(text); setRecording(null) }
    } catch (e) {
      if (request.signal.aborted) setError('Transcription cancelled. Your notes are unchanged.')
      else setError((e as Error).message || 'Could not transcribe this recording. Your notes are unchanged.')
    } finally {
      if (controller.current === request) {
        controller.current = null; setActive(false); callbacks.current.onActive(false)
      }
    }
  }

  return <div className="intake-audio-upload">
    <input ref={input} className="visually-hidden" type="file" aria-label="Audio recording" tabIndex={-1}
      accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.opus,.flac,.webm,.aif,.aiff,.wma" disabled={disabled || active}
      onChange={e => { const file = e.target.files?.[0]; e.target.value = ''; if (file) void upload(file) }} />
    <div className="row">
      <button className="ghost" disabled={disabled || active} onClick={() => input.current?.click()}><Icon name={active ? 'clock' : 'file'} size={16} />{active ? 'Transcribing audio…' : 'Upload audio'}</button>
      {active && <button className="ghost" aria-label="Cancel audio transcription" onClick={() => controller.current?.abort()}><Icon name="close" size={16} /></button>}
    </div>
    {active && <span className="muted small" role="status">{recording?.name}</span>}
    {error && <div className="intake-audio-error" role="alert">
      <span>{error}</span>
      {recording && !!recording.size && recording.size <= MAX_AUDIO_BYTES && (recording.type.startsWith('audio/') || AUDIO_EXTENSIONS.test(recording.name)) &&
        <button className="link" disabled={disabled || active} onClick={() => void upload(recording)}>Retry transcription</button>}
    </div>}
  </div>
}
